<script setup lang="ts">
import BaseInputText from '../../../../base/BaseInputText.vue'
import BaseInputCheckbox from '../../../../base/BaseInputCheckbox.vue'
import { onMounted, watch } from 'vue'
import { useForm } from 'vee-validate'
import BaseInputCombobox from '../../../../base/BaseInputCombobox.vue'
import { IdentificationIcon, UserCircleIcon } from '@heroicons/vue/solid'
import { genderOptions, getAllCountries, pepOptions } from '../../../../../data'
import BaseInputDateSeparate from '../../../../base/BaseInputDateSeparate.vue'
import BaseInputSelect from '../../../../base/BaseInputSelect.vue'
import { useLegalAccountFormState } from '../../../../../utilities/account/useAccountFormLegal'
import { LegalPerson } from './forms'
import { useVModel } from '@vueuse/core'
import { isPastPepOptionShown, isRequireAdditionalPepInformationEnabled } from '../../../../../utilities/usePepSettings'
import BaseInputRadio from '../../../../base/BaseInputRadio.vue'
import {
    isRequiredNaturalProfileAttributesForInvestment,
    isRequiredRequiredNaturalProfileAttributes,
} from '../../../../../validation'
import { isNaturalProfileAttributeLocked } from '../../../../../utilities/account/useAccountDataPersonal'

const form = useForm({
    validationSchema: {
        first_name: 'required',
        last_name: 'required',
        email: 'required',
        nationality: 'required',
        country_of_birth: 'required',
        city_of_birth: 'required',
        date_of_birth: 'required',
        gender: 'required',
        pep: { one_of: [true, false] },
        pep_past: isPastPepOptionShown() ? { one_of: [true, false] } : '',
        pep_past_position: isRequireAdditionalPepInformationEnabled() ? 'required_if:pep_past' : '',
        pep_position: isRequireAdditionalPepInformationEnabled() && isPastPepOptionShown() ? 'required_if:pep' : '',
    },
})

const formState = useLegalAccountFormState()

const props = defineProps<{
    modelValue: LegalPerson
    isManager?: boolean
    isBeneficialOwner?: boolean
    disableEmail?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const formValues = useVModel(props, 'modelValue', emit)

watch(formValues, () => {
    formValues.value.is_manager = true
})

onMounted(() => {
    if (props.isManager) {
        formValues.value.is_manager = true
    }
    // @ts-ignore
    formState.currentForm = form
})
</script>

<template>
    <div>
        <div class="grid grid-cols-1 gap-x-4 gap-y-3 pt-2 sm:grid-cols-6">
            <div class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.first_name"
                    required
                    name="first_name"
                    :label="$t('account.first_name')"></BaseInputText>
            </div>

            <div class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.last_name"
                    required
                    name="last_name"
                    :label="$t('account.last_name')"></BaseInputText>
            </div>

            <div class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.email"
                    required
                    :disabled="props.disableEmail"
                    name="email"
                    :label="$t('account.email')"></BaseInputText>
            </div>

            <div class="sm:col-span-3">
                <BaseInputCombobox
                    v-model="formValues.nationality"
                    required
                    :icon="IdentificationIcon"
                    name="nationality"
                    :label="$t('account.nationality')"
                    :options="getAllCountries()"
                    nullable />
            </div>

            <div class="sm:col-span-3">
                <BaseInputCombobox
                    v-model="formValues.country_of_birth"
                    required
                    :icon="IdentificationIcon"
                    name="country_of_birth"
                    :label="$t('beneficial_owner.country_of_birth')"
                    :options="getAllCountries()"
                    nullable />
            </div>

            <div class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.city_of_birth"
                    required
                    name="city_of_birth"
                    :label="$t('beneficial_owner.city_of_birth')"></BaseInputText>
            </div>

            <div class="sm:col-span-3">
                <BaseInputSelect
                    v-model="formValues.gender"
                    :icon="UserCircleIcon"
                    required
                    name="gender"
                    :label="$t('account.gender')"
                    :options="genderOptions" />
            </div>
            <div class="sm:col-span-3">
                <BaseInputDateSeparate
                    v-model="formValues.date_of_birth"
                    required
                    name="date_of_birth"
                    :label="$t('account.date_of_birth')" />
            </div>
            <div v-if="isManager" :class="['flex flex-wrap items-center sm:col-span-3']">
                <BaseInputRadio
                    v-model="formValues.pep"
                    :required="isRequiredRequiredNaturalProfileAttributes('pep')"
                    :disabled="isNaturalProfileAttributeLocked('pep')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('pep')"
                    name="pep"
                    :label="$t('account.pep')"
                    :options="pepOptions" />
            </div>
            <div v-if="isManager && formValues.pep && isRequireAdditionalPepInformationEnabled()" class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.pep_position"
                    required
                    :disabled="isNaturalProfileAttributeLocked('pep')"
                    name="pep_position"
                    :icon="IdentificationIcon"
                    :label="$t('account.pep_position')" />
            </div>
            <div
                v-if="isManager && isPastPepOptionShown()"
                :class="[
                    'flex flex-wrap items-center sm:col-span-3',
                    {
                        'sm:col-start-1': isRequireAdditionalPepInformationEnabled(),
                    },
                ]">
                <BaseInputRadio
                    v-model="formValues.pep_past"
                    :required="isRequiredRequiredNaturalProfileAttributes('pep')"
                    :disabled="isNaturalProfileAttributeLocked('pep')"
                    :required-2="isRequiredNaturalProfileAttributesForInvestment('pep')"
                    name="pep_past"
                    :label="$t('account.pep_past')"
                    :options="pepOptions" />
            </div>
            <div v-if="isManager && formValues.pep_past && isPastPepOptionShown()" class="sm:col-span-3">
                <BaseInputText
                    v-model="formValues.pep_past_position"
                    required
                    rules="required"
                    :disabled="isNaturalProfileAttributeLocked('pep')"
                    name="pep_past_position"
                    :icon="IdentificationIcon"
                    :label="$t('account.pep_position')" />
            </div>
            <div v-if="!props.isBeneficialOwner" class="grid w-full grid-cols-1 gap-y-4 pt-2 sm:col-span-3">
                <BaseInputCheckbox
                    v-model="formValues.is_manager"
                    disabled
                    :label="$t('legal_account_manager_beneficial_owner.manager')"
                    name="is_manager"></BaseInputCheckbox>
                <BaseInputCheckbox
                    v-model="formValues.is_beneficial_owner"
                    :label="$t('legal_account_manager_beneficial_owner.beneficial_owner')"
                    name="is_beneficial_owner"></BaseInputCheckbox>
            </div>
        </div>
    </div>
</template>
